import { useCallback, useEffect, useState } from 'react';

import { useInView } from 'react-intersection-observer';

/**
 * Infinite scroll for the ambient notes list. Uses IntersectionObserver with
 * the scroll container as root so scrolling to the bottom triggers fetchNextPage.
 * Uses isFetchingNextPage (not isLoading) to avoid stale-closure blocking when
 * the date changes. Requires hasPages so we don't fetch "next" before first load.
 * Returns sentinel ref (attach to element at bottom of list) and setScrollRoot
 * (use in a callback ref on the scroll container so the observer root is set
 * when the container mounts).
 */
export const useAmbientInfiniteScroll = (
  hasNextPage: boolean | undefined,
  isFetchingNextPage: boolean,
  hasPages: boolean,
  fetchNextPage: () => void,
) => {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const setScrollRoot = useCallback((node: HTMLElement | null) => {
    setRoot(node);
  }, []);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { ref, inView } = useInView({
    root,
    rootMargin: '0px',
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView && !isFetchingNextPage && hasNextPage && hasPages) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, isFetchingNextPage, hasNextPage, hasPages]);

  return { ref, setScrollRoot };
};
